import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { type Locale } from "@/i18n/config";
import { BASE_URL, pageHead } from "@/i18n/head";
import { pathFor } from "@/i18n/slugMap";
import { videnStrings } from "@/i18n/content/viden";
import { ordbogAlphabet, ordbogTerms, termPath, termsByLetter } from "@/data/ordbog";

export function videnHead(locale: Locale) {
  const t = videnStrings[locale];
  const definedTermSet = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": BASE_URL + pathFor("viden", locale),
    name: t.hero.title,
    description: t.meta.description,
    hasDefinedTerm: ordbogTerms.map((term) => ({
      "@type": "DefinedTerm",
      name: term.term,
      description: term.short,
      url: BASE_URL + termPath(term, locale),
    })),
  };
  return pageHead("viden", locale, t.meta, {
    breadcrumbName: t.hero.eyebrow,
    schema: [definedTermSet],
  });
}

export function VidenPage({ locale }: { locale: Locale }) {
  const t = videnStrings[locale];
  const groups = termsByLetter();
  const lettersWithTerms = new Set(groups.map((g) => g.letter));

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary/60 to-background">
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-20 md:py-28">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">
            {t.hero.eyebrow}
          </p>
          <h1 className="mt-3 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-foreground/85 leading-relaxed">
            {t.hero.intro}
          </p>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <p className="text-sm font-semibold text-section-light-foreground/70 mb-3">{t.jumpLabel}</p>
          <div className="flex flex-wrap gap-1.5">
            {ordbogAlphabet.map((letter) =>
              lettersWithTerms.has(letter) ? (
                <a
                  key={letter}
                  href={`#bogstav-${letter.toLowerCase()}`}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  {letter}
                </a>
              ) : (
                <span
                  key={letter}
                  aria-hidden
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-section-light-foreground/5 text-sm font-medium text-section-light-foreground/30"
                >
                  {letter}
                </span>
              ),
            )}
          </div>

          <div className="mt-12 space-y-12">
            {groups.map((g) => (
              <div key={g.letter} id={`bogstav-${g.letter.toLowerCase()}`} className="scroll-mt-28">
                <h2 className="text-2xl font-bold text-primary">{g.letter}</h2>
                <div className="mt-4 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {g.terms.map((term) => (
                    <Link
                      key={term.slug}
                      to={termPath(term, locale)}
                      className="group rounded-2xl bg-background/5 border border-section-light-foreground/10 p-7 flex flex-col hover:border-primary/40 hover:shadow-lg transition-all"
                    >
                      <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                        {term.term}
                      </h3>
                      <p className="mt-2 text-sm text-section-light-foreground/75 leading-relaxed flex-1">
                        {term.short}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        {t.relatedHeading}
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">{t.cta.heading}</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">{t.cta.body}</p>
          <Link
            to={pathFor("kontakt", locale)}
            className="mt-8 inline-block rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t.cta.button}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
